import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { 
  RefreshCw, Eye, MapPin, UserPlus, Download, Settings, 
  CheckSquare, Square, MoreHorizontal, X, CheckCircle
} from 'lucide-react';
import { getJobCards } from '../../../../models/externalModel';
import { getFieldWorkers, createAssignment, getAllLocalAssignments, getAssignmentActivities } from '../../../../models/mmsModel';
import api from '../../../../services/api';
import AdvancedJobCardFilters from '../../../../components/mms/filters/AdvancedJobCardFilters';
import JobCardStatsWidget from '../../../../components/mms/stats/JobCardStatsWidget';
import JobCardDetailModal from '../../../../components/mms/modals/JobCardDetailModal';
import LocalAssignmentDetailModal from '../../../../components/mms/modals/LocalAssignmentDetailModal';
import BulkAssignmentModal from '../../../../components/mms/modals/BulkAssignmentModal';
import { useJobCardsWithFilters } from '../../../../hooks/useMmsLookups';

const EnhancedJobCardsList = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('edams'); // 'edams' or 'local'
  
  // State management
  const { jobCards, loading, filters, setFilters, refresh } = useJobCardsWithFilters();
  const [workers, setWorkers] = useState([]);
  const [initialWorkers, setInitialWorkers] = useState([]); // Store initial workers list
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]); // Separate search results
  const [isSearching, setIsSearching] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [showStats, setShowStats] = useState(true);
  
  // Local assignments state
  const [localAssignments, setLocalAssignments] = useState([]);
  const [loadingLocalAssignments, setLoadingLocalAssignments] = useState(false);
  
  // Modal states
  const [detailModal, setDetailModal] = useState({ show: false, job: null, isLocal: false });
  const [bulkAssignModal, setBulkAssignModal] = useState(false);
  const [columnSettings, setColumnSettings] = useState(false);
  const [closeJobModal, setCloseJobModal] = useState({ show: false, assignment: null, comment: '', isProcessing: false });
  
  // Debounced search across all dates
  const handleSearch = useCallback(async (term) => {
    if (!term || term.length < 3) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    
    setIsSearching(true);
    try {
      // Search without date restrictions - pass empty dates and search term
      const results = await getJobCards({ 
        start_date: '', 
        end_date: '',
        search: term 
      });
      if (results) {
        setSearchResults(results);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  }, []);
  
  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchTerm);
    }, 500); // Wait 500ms after user stops typing
    
    return () => clearTimeout(timer);
  }, [searchTerm, handleSearch]);
  
  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState({
    jobcard_no: true,
    description: true,
    customer: false,
    property: true,
    issued_date: true,
    priority: false,
    station: true,
    assigned_to: true,
    status: true,
    actions: true
  });

  // Fetch workers
  React.useEffect(() => {
    fetchWorkers();
  }, []);

  // Fetch local assignments always (needed for EDAMS tab to show local assignments)
  useEffect(() => {
    fetchLocalAssignments();
  }, []);

  const fetchLocalAssignments = useCallback(async () => {
    try {
      setLoadingLocalAssignments(true);
      const response = await getAllLocalAssignments();
      let assignments = [];
      if (response && response.data && response.data.assignments) {
        assignments = response.data.assignments;
        setLocalAssignments(assignments);
      } else if (response && response.assignments) {
        assignments = response.assignments;
        setLocalAssignments(assignments);
      } else {
        setLocalAssignments([]);
      }
      
      // Log first local assignment for debugging
      if (assignments.length > 0) {
        console.log('=== FIRST LOCAL ASSIGNMENT ===', assignments[0]);
      }
    } catch (error) {
      console.error('Error fetching local assignments:', error);
      toast.error('Failed to load local assignments');
      setLocalAssignments([]);
    } finally {
      setLoadingLocalAssignments(false);
    }
  }, []);

  // Create mapping of jobcard_no to local assigned worker name
  const localAssignmentMap = useMemo(() => {
    const map = {};
    localAssignments.forEach(assignment => {
      const jobcardNo = assignment.JOBCARD_NO || assignment.jobcard_no;
      if (jobcardNo) {
        const workerName = assignment.ASSIGNED_TO_NAME || assignment.worker_name;
        if (workerName) {
          map[jobcardNo] = workerName;
        }
      }
    });
    return map;
  }, [localAssignments]);

  // Generate workers list from job cards (same as dashboard)
  const generateWorkersFromJobs = useCallback((jobCards) => {
    if (!Array.isArray(jobCards) || jobCards.length === 0) return [];

    const workersMap = new Map();

    jobCards.forEach(job => {
      if (job.ASSIGNED_TO_NAME && job.ASSIGNED_TO_NAME.trim() !== '') {
        const workerName = job.ASSIGNED_TO_NAME;
        const employeeNo = job.ASSIGNEDTO; // Get the EDAMS employee number
        
        if (!workersMap.has(workerName)) {
          workersMap.set(workerName, {
            worker_id: workerName, // Use worker name as ID (unique identifier)
            worker_name: workerName,
            worker_employee_id: employeeNo, // Add employee number for profile lookup
            active_jobs: 0,
            completed_jobs: 0
          });
        }

        const worker = workersMap.get(workerName);
        const status = parseInt(job.OM_STATUS);
        if (status === 6) {
          worker.completed_jobs++;
        } else {
          worker.active_jobs++;
        }
      }
    });

    return Array.from(workersMap.values())
      .sort((a, b) => b.active_jobs - a.active_jobs);
  }, []);

  // Generate initial workers list only once when job cards first load
  React.useEffect(() => {
    if (jobCards.length > 0 && initialWorkers.length === 0) {
      const workersList = generateWorkersFromJobs(jobCards);
      setInitialWorkers(workersList);
      setWorkers(workersList);
    }
  }, [jobCards, initialWorkers.length, generateWorkersFromJobs]);

  const fetchWorkers = useCallback(async () => {
    try {
      const response = await getFieldWorkers({ limit: 100 });
      if (response && response.data && response.data.data) {
        setWorkers(response.data.data.filter(w => w.worker_status === '1'));
      }
    } catch (err) {
      console.error('Fetch workers error:', err);
    }
  }, []);

  // Selection handlers
  const toggleJobSelection = (jobCardNo) => {
    setSelectedJobs(prev =>
      prev.includes(jobCardNo)
        ? prev.filter(id => id !== jobCardNo)
        : [...prev, jobCardNo]
    );
  };

  const toggleSelectAll = () => {
    if (selectedJobs.length === filteredJobCards.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(filteredJobCards.map(job => job.JOBCARD_NO));
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'Job Card No',
      'Description',
      'Customer',
      'Phone',
      'Property',
      'Address',
      'Issued Date',
      'Priority',
      'Station',
      'Assigned To',
      'Status'
    ];

    const rows = filteredJobCards.map(job => [
      job.JOBCARD_NO,
      job.JOB_DESCRIPTION || '',
      job.CUSTOMER_NAME || '',
      job.CELL_TEL_NO || '',
      job.STAND_NO || job.PROP_REF || '',
      job.ADDRESS || '',
      formatDate(job.ISSUED_DATETIME),
      job.PRIORITY || '',
      job.STATION_NAME || '',
      job.ASSIGNED_TO_NAME || '',
      getStatusName(job.OM_STATUS)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `job-cards-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Exported ${filteredJobCards.length} job cards to CSV`);
  };

  // Close job handler
  const handleCloseJob = async () => {
    if (!closeJobModal.assignment) return;

    try {
      setCloseJobModal(prev => ({ ...prev, isProcessing: true }));

      // Record the close activity
      await api.post(`/api/mms/assignments/${closeJobModal.assignment.assignment_id}/activities`, {
        activity_type: 'job_closed',
        notes: `Job closed: ${closeJobModal.comment || 'No comment provided'}`,
        latitude: null,
        longitude: null
      });

      // Update assignment status to completed
      await api.put(`/api/mms/assignments/${closeJobModal.assignment.assignment_id}/status`, {
        status: 'completed'
      });

      toast.success('Job closed successfully');
      
      // Update local state immediately
      setLocalAssignments(prev => 
        prev.map(assignment => 
          assignment.assignment_id === closeJobModal.assignment.assignment_id
            ? { ...assignment, status: 'completed' }
            : assignment
        )
      );
      
      setCloseJobModal({ show: false, assignment: null, comment: '', isProcessing: false });
    } catch (error) {
      console.error('Error closing job:', error);
      toast.error('Failed to close job: ' + (error.response?.data?.message || error.message));
    } finally {
      setCloseJobModal(prev => ({ ...prev, isProcessing: false }));
    }
  };

  // Utility functions
  const getStatusBadge = (status) => {
    const statusMap = {
      0: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
      1: { label: 'Assigned', color: 'bg-blue-100 text-blue-800 border-blue-300' },
      3: { label: 'In Progress', color: 'bg-purple-100 text-purple-800 border-purple-300' },
      4: { label: 'Scheduled', color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
      5: { label: 'On Hold', color: 'bg-orange-100 text-orange-800 border-orange-300' },
      6: { label: 'Completed', color: 'bg-green-100 text-green-800 border-green-300' }
    };
    
    const statusInfo = statusMap[status] || { 
      label: `Status ${status}`, 
      color: 'bg-gray-100 text-gray-800 border-gray-300' 
    };
    
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const getStatusName = (status) => {
    const statusMap = {
      0: 'Pending',
      1: 'Assigned',
      3: 'In Progress',
      4: 'Scheduled',
      5: 'On Hold',
      6: 'Completed'
    };
    return statusMap[status] || `Status ${status}`;
  };

  const mapLocalStatusToOmStatus = (status) => {
    const statusMap = {
      'assigned': 1,
      'active': 1,
      'in-progress': 3,
      'in_progress': 3,
      'completed': 6,
      'on-hold': 5,
      'on_hold': 5,
      'cancelled': 0,
      'pending': 0
    };
    return statusMap[status?.toLowerCase()] ?? 1;
  };

  const getPriorityBadge = (priorityId) => {
    const priorityMap = {
      0: { label: 'Emergency', color: 'bg-red-100 text-red-800 border-red-300' },
      1: { label: 'Urgent', color: 'bg-orange-100 text-orange-800 border-orange-300' },
      2: { label: 'High', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
      3: { label: 'Normal', color: 'bg-blue-100 text-blue-800 border-blue-300' },
      4: { label: 'Low', color: 'bg-gray-100 text-gray-800 border-gray-300' }
    };

    const priority = priorityMap[priorityId] || { label: 'Normal', color: 'bg-gray-100 text-gray-800 border-gray-300' };
    
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${priority.color}`}>
        {priority.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  // Filter job cards - use search results when searching, otherwise use filtered jobCards
  // Don't show stale data while loading
  const dataToFilter = loading ? [] : (searchTerm.length >= 3 ? searchResults : jobCards);
  
  const filteredJobCards = dataToFilter.filter(job => {
    // If using search results, they're already filtered by search term
    const matchesSearch = searchTerm.length >= 3 ? true : (
      !searchTerm || 
      job.JOBCARD_NO?.toString().includes(searchTerm) ||
      job.JOB_DESCRIPTION?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.APPLICATION_DESCRIPTION?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.CUSTOMER_NAME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.STAND_NO?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.ADDRESS?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // Apply filters
    const matchesStatus = !filters.status || job.OM_STATUS === filters.status;
    const matchesPriority = !filters.priority || job.PRIORITY_RANK?.toString() === filters.priority;
    const matchesAssignedTo = !filters.assigned_to || job.ASSIGNED_TO_NAME === filters.assigned_to;
    const matchesStation = !filters.station_no || job.STATION_NO?.toString() === filters.station_no;
    const matchesApplicationTypes = !filters.application_types || filters.application_types.length === 0 || 
      filters.application_types.includes(job.APPLICATION_TYPE_ID?.toString());
    
    // Date range filter - bypass if searching with 3+ characters
    const matchesDateRange = searchTerm.length >= 3 ? true : (() => {
      if (!filters.start_date && !filters.end_date) return true;
      if (!job.ISSUED_DATETIME) return false; // Exclude jobs without dates
      
      const jobDate = new Date(job.ISSUED_DATETIME);
      if (isNaN(jobDate.getTime())) return false; // Invalid date
      
      if (filters.start_date) {
        const startDate = new Date(filters.start_date + 'T00:00:00');
        if (jobDate < startDate) return false;
      }
      
      if (filters.end_date) {
        const endDate = new Date(filters.end_date + 'T23:59:59');
        if (jobDate > endDate) return false;
      }
      
      return true;
    })();
    
    return matchesSearch && matchesStatus && matchesPriority && matchesAssignedTo && 
           matchesStation && matchesApplicationTypes && matchesDateRange;
  });

  // Pagination
  const totalPages = Math.ceil(filteredJobCards.length / pageSize);
  const paginatedJobCards = filteredJobCards.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Filter local assignments
  const filteredLocalAssignments = localAssignments.filter(assignment => {
    const matchesSearch = !searchTerm || 
      assignment.JOBCARD_NO?.toString().includes(searchTerm) ||
      assignment.JOB_DESCRIPTION?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.APPLICATION_DESCRIPTION?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.ASSIGNED_TO_NAME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.ADDRESS?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  // Pagination for local assignments
  const totalLocalPages = Math.ceil(filteredLocalAssignments.length / pageSize);
  const paginatedLocalAssignments = filteredLocalAssignments.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4 py-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Job Cards Management</h1>
                <p className="text-xs text-gray-500">Enterprise-level job card filtering and management</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="mt-4 border-b border-gray-200">
              <nav className="flex space-x-8">
                <button
                  onClick={() => {
                    setActiveTab('edams');
                    setCurrentPage(1);
                    setSearchTerm('');
                    setSearchResults([]);
                    setSelectedJobs([]);
                  }}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'edams'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  EDAMS Jobs
                </button>
                <button
                  onClick={() => {
                    setActiveTab('local');
                    setCurrentPage(1);
                    setSearchTerm('');
                    setSelectedJobs([]);
                    fetchLocalAssignments();
                  }}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'local'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Local Assignments ({localAssignments.length})
                </button>
              </nav>
            </div>
          </div>

            {/* Action Buttons and Search */}
            <div className="flex items-center space-x-2 ml-4">
              {/* Search Bar */}
              <div className="relative w-[576px]">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {isSearching ? (
                    <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />
                  ) : (
                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Search across all job cards (min 3 characters)..."
                />
              </div>

              {/* Buttons */}
              {selectedJobs.length > 0 && (
                <button
                  onClick={() => setBulkAssignModal(true)}
                  className="flex items-center px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm whitespace-nowrap"
                >
                  <UserPlus className="h-4 w-4 mr-1" />
                  Bulk Assign ({selectedJobs.length})
                </button>
              )}

              <button
                onClick={refresh}
                disabled={loading}
                className="flex items-center px-3 py-2 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Advanced Filters - Only show for EDAMS tab */}
        {activeTab === 'edams' && (
          <AdvancedJobCardFilters
            filters={filters}
            onFiltersChange={setFilters}
            workers={initialWorkers}
            onApply={() => {
              setCurrentPage(1);
              setSearchTerm(''); // Clear search when applying filters
              setSearchResults([]); // Clear search results
              setSelectedJobs([]); // Clear selected jobs
              refresh();
            }}
            onReset={() => {
              setCurrentPage(1);
              setSearchTerm('');
              setSearchResults([]);
              setSelectedJobs([]);
            }}
          />
        )}

        {/* Results Summary */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">
              {activeTab === 'edams' 
                ? `Showing ${Math.min(pageSize, filteredJobCards.length)} of ${filteredJobCards.length} job cards`
                : `Showing ${Math.min(pageSize, filteredLocalAssignments.length)} of ${filteredLocalAssignments.length} local assignments`
              }
            </span>
            {selectedJobs.length > 0 && (
              <>
                <div className="h-4 w-px bg-gray-300" />
                <span className="text-sm font-medium text-blue-600">
                  {selectedJobs.length} selected
                </span>
                <button
                  onClick={() => setSelectedJobs([])}
                  className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear selection
                </button>
              </>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="text-sm px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {[10, 20, 50, 100, 200].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Job Cards Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
                <tr>
                  {activeTab === 'edams' && (
                    <th className="px-3 py-3 text-left">
                      <button
                        onClick={toggleSelectAll}
                        className="flex items-center justify-center w-5 h-5 border-2 border-gray-400 rounded hover:border-blue-600 transition-colors"
                      >
                        {selectedJobs.length === filteredJobCards.length && filteredJobCards.length > 0 ? (
                          <CheckSquare className="h-5 w-5 text-blue-600" />
                        ) : (
                          <Square className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </th>
                  )}
                  {visibleColumns.jobcard_no && <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-blue-600">Job Card</th>}
                  {visibleColumns.description && <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-blue-600">Description</th>}
                  {visibleColumns.customer && <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-blue-600">Customer</th>}
                  {visibleColumns.property && <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-blue-600">Property</th>}
                  {visibleColumns.issued_date && <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-blue-600">{activeTab === 'local' ? 'Assigned' : 'Issued'}</th>}
                  {visibleColumns.priority && <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-blue-600">Priority</th>}
                  {visibleColumns.station && <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-blue-600">Station</th>}
                  {visibleColumns.assigned_to && <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-blue-600">Assigned To</th>}
                  {visibleColumns.status && <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-blue-600">Status</th>}
                  {visibleColumns.actions && <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-blue-600">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {/* EDAMS Jobs Table */}
                {activeTab === 'edams' && (
                  <>
                    {loading && paginatedJobCards.length === 0 && (
                      <tr>
                        <td colSpan="11" className="px-3 py-8 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <RefreshCw className="animate-spin h-5 w-5 text-blue-500" />
                            <span className="text-sm text-gray-600">Loading job cards...</span>
                          </div>
                        </td>
                      </tr>
                    )}
                    
                    {!loading && paginatedJobCards.length === 0 && (
                      <tr>
                        <td colSpan="11" className="px-3 py-8 text-center">
                          <p className="text-gray-600">No job cards found</p>
                        </td>
                      </tr>
                    )}
                    
                    {paginatedJobCards.map(job => (
                      <tr 
                        key={job.JOBCARD_NO} 
                        className={`hover:bg-blue-50 transition-colors ${selectedJobs.includes(job.JOBCARD_NO) ? 'bg-blue-50' : ''}`}
                      >
                        <td className="px-3 py-3">
                          <button
                            onClick={() => toggleJobSelection(job.JOBCARD_NO)}
                            className="flex items-center justify-center w-5 h-5 border-2 border-gray-400 rounded hover:border-blue-600 transition-colors"
                          >
                            {selectedJobs.includes(job.JOBCARD_NO) ? (
                              <CheckSquare className="h-5 w-5 text-blue-600" />
                            ) : (
                              <Square className="h-5 w-5 text-gray-400" />
                            )}
                          </button>
                        </td>
                    {visibleColumns.jobcard_no && (
                      <td className="px-3 py-3 text-sm font-bold text-blue-600">
                        #{job.JOBCARD_NO}
                      </td>
                    )}
                    {visibleColumns.description && (
                      <td className="px-3 py-3 text-sm text-gray-700">
                        <div className="max-w-xs truncate" title={job.JOB_DESCRIPTION}>
                          {job.JOB_DESCRIPTION || 'No description'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {job.APPLICATION_TYPE_NAME || 'No type'}
                        </div>
                      </td>
                    )}
                    {visibleColumns.customer && (
                      <td className="px-3 py-3 text-sm text-gray-700">
                        <div className="font-medium">{job.CUSTOMER_NAME || 'Unknown'}</div>
                        {job.CELL_TEL_NO && <div className="text-xs text-gray-500">{job.CELL_TEL_NO}</div>}
                      </td>
                    )}
                    {visibleColumns.property && (
                      <td className="px-3 py-3 text-sm text-gray-700">
                        <div className="font-medium">{job.STAND_NO || job.PROP_REF || 'N/A'}</div>
                        {job.ADDRESS && <div className="text-xs text-gray-500 max-w-xs truncate" title={job.ADDRESS}>{job.ADDRESS}</div>}
                      </td>
                    )}
                    {visibleColumns.issued_date && (
                      <td className="px-3 py-3 text-sm text-gray-700">
                        {formatDate(job.ISSUED_DATETIME)}
                      </td>
                    )}
                    {visibleColumns.priority && (
                      <td className="px-3 py-3 text-sm">
                        {getPriorityBadge(job.PRIORITY_ID || job.PRIORITY_RANK)}
                      </td>
                    )}
                    {visibleColumns.station && (
                      <td className="px-3 py-3 text-sm text-gray-700">
                        {job.STATION_NAME || 'N/A'}
                      </td>
                    )}
                    {visibleColumns.assigned_to && (
                      <td className="px-3 py-3 text-sm text-gray-700">
                        <div className="flex flex-col">
                          <div>{job.ASSIGNED_TO_NAME || 'Unassigned'}</div>
                          {localAssignmentMap[job.JOBCARD_NO] && (
                            <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                              {localAssignmentMap[job.JOBCARD_NO]}
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                    {visibleColumns.status && (
                      <td className="px-3 py-3 text-sm">
                        {getStatusBadge(job.OM_STATUS)}
                      </td>
                    )}
                    {visibleColumns.actions && (
                      <td className="px-3 py-3 text-sm">
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => {
                              setDetailModal({ show: true, job, isLocal: false });
                            }}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="View Details"
                          >
                            <Eye size={18} />
                          </button>
                          
                          {job.LATITUDE && job.LONGITUDE && (
                            <button
                              onClick={() => window.open(`https://www.google.com/maps?q=${job.LATITUDE},${job.LONGITUDE}`, '_blank')}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="View on Map"
                            >
                              <MapPin size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                      </tr>
                    ))}
                  </>
                )}

                {/* Local Assignments Table */}
                {activeTab === 'local' && (
                  <>
                    {loadingLocalAssignments && paginatedLocalAssignments.length === 0 && (
                      <tr>
                        <td colSpan="10" className="px-3 py-8 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <RefreshCw className="animate-spin h-5 w-5 text-blue-500" />
                            <span className="text-sm text-gray-600">Loading local assignments...</span>
                          </div>
                        </td>
                      </tr>
                    )}
                    
                    {!loadingLocalAssignments && paginatedLocalAssignments.length === 0 && (
                      <tr>
                        <td colSpan="10" className="px-3 py-8 text-center">
                          <p className="text-gray-600">No local assignments found</p>
                        </td>
                      </tr>
                    )}
                    
                    {paginatedLocalAssignments.map(assignment => (
                      <tr 
                        key={assignment.assignment_id} 
                        className="hover:bg-blue-50 transition-colors"
                      >
                        {visibleColumns.jobcard_no && (
                          <td className="px-3 py-3 text-sm font-bold text-blue-600">
                            #{assignment.JOBCARD_NO || assignment.jobcard_no}
                          </td>
                        )}
                        {visibleColumns.description && (
                          <td className="px-3 py-3 text-sm text-gray-700">
                            <div className="max-w-xs truncate" title={assignment.JOB_DESCRIPTION || 'No description'}>
                              {assignment.JOB_DESCRIPTION || 'No description'}
                            </div>
                          </td>
                        )}
                        {visibleColumns.customer && (
                          <td className="px-3 py-3 text-sm text-gray-700">
                            <div className="font-medium">{assignment.CUSTOMER_NAME || 'N/A'}</div>
                            {assignment.CELL_TEL_NO && <div className="text-xs text-gray-500">{assignment.CELL_TEL_NO}</div>}
                          </td>
                        )}
                        {visibleColumns.property && (
                          <td className="px-3 py-3 text-sm text-gray-700">
                            <div className="font-medium">{assignment.STAND_NO || assignment.PROP_REF || 'N/A'}</div>
                            {assignment.ADDRESS && <div className="text-xs text-gray-500 max-w-xs truncate" title={assignment.ADDRESS}>{assignment.ADDRESS}</div>}
                          </td>
                        )}
                        {visibleColumns.issued_date && (
                          <td className="px-3 py-3 text-sm text-gray-700">
                            {formatDate(assignment.assigned_at || assignment.ISSUED_DATETIME)}
                          </td>
                        )}
                        {visibleColumns.priority && (
                          <td className="px-3 py-3 text-sm">
                            {getPriorityBadge(assignment.PRIORITY_RANK || assignment.PRIORITY_ID || 3)}
                          </td>
                        )}
                        {visibleColumns.station && (
                          <td className="px-3 py-3 text-sm text-gray-700">
                            {assignment.STATION_NAME || 'N/A'}
                          </td>
                        )}
                        {visibleColumns.assigned_to && (
                          <td className="px-3 py-3 text-sm text-gray-700">
                            {assignment.ASSIGNED_TO_NAME || assignment.worker_name || 'Unassigned'}
                          </td>
                        )}
                        {visibleColumns.status && (
                          <td className="px-3 py-3 text-sm">
                            {activeTab === 'local' 
                              ? getStatusBadge(mapLocalStatusToOmStatus(assignment.status || assignment.assignment_status))
                              : getStatusBadge(assignment.OM_STATUS)
                            }
                          </td>
                        )}
                        {visibleColumns.actions && (
                          <td className="px-3 py-3 text-sm">
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => {
                                  setDetailModal({ show: true, job: assignment, isLocal: true });
                                }}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="View Details"
                              >
                                <Eye size={18} />
                              </button>
                              
                              {(assignment.LATITUDE || assignment.property_latitude) && (assignment.LONGITUDE || assignment.property_longitude) && (
                                <button
                                  onClick={() => window.open(`https://www.google.com/maps?q=${assignment.LATITUDE || assignment.property_latitude},${assignment.LONGITUDE || assignment.property_longitude}`, '_blank')}
                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                                  title="View on Map"
                                >
                                  <MapPin size={18} />
                                </button>
                              )}

                              {(assignment.assignment_status !== 'completed' && assignment.OM_STATUS !== 6) && (
                                <button
                                  onClick={() => setCloseJobModal({ show: true, assignment, comment: '', isProcessing: false })}
                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                                  title="Close Job"
                                >
                                  <CheckCircle size={18} />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {((activeTab === 'edams' && filteredJobCards.length > 0) || (activeTab === 'local' && filteredLocalAssignments.length > 0)) && (
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                {activeTab === 'edams' 
                  ? `Showing ${((currentPage - 1) * pageSize) + 1} to ${Math.min(currentPage * pageSize, filteredJobCards.length)} of ${filteredJobCards.length} results`
                  : `Showing ${((currentPage - 1) * pageSize) + 1} to ${Math.min(currentPage * pageSize, filteredLocalAssignments.length)} of ${filteredLocalAssignments.length} results`
                }
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 bg-white text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 bg-white text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, activeTab === 'edams' ? totalPages : totalLocalPages) }, (_, i) => {
                    const totalPagesForTab = activeTab === 'edams' ? totalPages : totalLocalPages;
                    let pageNum;
                    if (totalPagesForTab <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPagesForTab - 2) {
                      pageNum = totalPagesForTab - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1.5 text-sm border rounded ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(Math.min(activeTab === 'edams' ? totalPages : totalLocalPages, currentPage + 1))}
                  disabled={currentPage === (activeTab === 'edams' ? totalPages : totalLocalPages)}
                  className="px-3 py-1.5 text-sm border border-gray-300 bg-white text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(activeTab === 'edams' ? totalPages : totalLocalPages)}
                  disabled={currentPage === (activeTab === 'edams' ? totalPages : totalLocalPages)}
                  className="px-3 py-1.5 text-sm border border-gray-300 bg-white text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {detailModal.show && !detailModal.isLocal && detailModal.job && (
        <JobCardDetailModal
          job={detailModal.job}
          onClose={() => setDetailModal({ show: false, job: null, isLocal: false })}
          onRefresh={refresh}
        />
      )}
      
      {detailModal.show && detailModal.isLocal && detailModal.job && (
        <LocalAssignmentDetailModal
          assignment={detailModal.job}
          onClose={() => setDetailModal({ show: false, job: null, isLocal: false })}
          onRefresh={fetchLocalAssignments}
        />
      )}

      {bulkAssignModal && (
        <BulkAssignmentModal
          jobCards={jobCards.filter(job => selectedJobs.includes(job.JOBCARD_NO))}
          workers={workers}
          onClose={() => setBulkAssignModal(false)}
          onSuccess={() => {
            setBulkAssignModal(false);
            setSelectedJobs([]);
            refresh();
          }}
        />
      )}

      {/* Close Job Modal */}
      {closeJobModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full">
            <div className="bg-white border-b border-gray-200 p-4 flex justify-between items-center rounded-t-md">
              <h3 className="font-semibold text-gray-900 flex items-center">
                <CheckCircle size={20} className="mr-2 text-green-600" />
                Close Job
              </h3>
              <button
                onClick={() => setCloseJobModal({ show: false, assignment: null, comment: '', isProcessing: false })}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Job Card:</strong> #{closeJobModal.assignment?.JOBCARD_NO || closeJobModal.assignment?.jobcard_no}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Customer:</strong> {closeJobModal.assignment?.CUSTOMER_NAME || 'N/A'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Optional Comment
                </label>
                <textarea
                  value={closeJobModal.comment}
                  onChange={(e) => setCloseJobModal(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder="Add any closing notes or comments..."
                  disabled={closeJobModal.isProcessing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50 disabled:opacity-50"
                  rows="3"
                />
              </div>

              <p className="text-xs text-gray-500">
                This action will record the job closure as an activity and update the job status to completed.
              </p>
            </div>

            <div className="bg-gray-50 px-6 py-3 rounded-b-md flex justify-end gap-2">
              <button
                onClick={() => setCloseJobModal({ show: false, assignment: null, comment: '', isProcessing: false })}
                disabled={closeJobModal.isProcessing}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleCloseJob}
                disabled={closeJobModal.isProcessing}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {closeJobModal.isProcessing ? (
                  <>
                    <RefreshCw size={16} className="mr-2 animate-spin" />
                    Closing...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Close Job
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EnhancedJobCardsList;
